import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FilterWidgetComponent } from '@app/shared/components/filter-widget/filter-widget.component';
import { PaginatorComponent } from '@app/shared/components/paginator/paginator.component';
import { ResponsiveTableComponent } from '@app/shared/components/table/responsive-table.component';
import { PatronColors, ToastService } from '@teorema/patron';
import { FilterField, FilterOperator, FindOptions, Ticket, TicketRevision } from 'src/app/models';
import { utils } from 'src/app/shared';
import { TcTicketRevisionService, TcTicketService } from 'src/app/shared/services';
import { MessageService } from 'primeng/api';
import { TicketDetail } from 'src/app/features/v3ndor/core/models/ticket.model';
import { V3NDOR_SERVICE_PROVIDERS } from 'src/app/features/v3ndor/v3ndor.providers';

@Component({
  templateUrl: './update-assistant.component.html',
  styleUrls: ['./update-assistant.component.scss'],
  standalone: false,
  // O `ticket-detail-modal` (e sua subárvore, incluindo o `customer-detail-modal`) depende dos
  // services do v3ndor, providos só na rota do v3ndor. Sem o conjunto completo aqui, a
  // construção de um filho estoura NG0201 e o corpo do modal não renderiza.
  providers: [MessageService, ...V3NDOR_SERVICE_PROVIDERS],
})
export class UpdateAssistantComponent implements OnInit {
  constructor(
    private revisionService: TcTicketRevisionService,
    private ticketService: TcTicketService,
    private fb: FormBuilder,
    private toastService: ToastService,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.formGroup = this.fb.group({
      project: [{ value: null, disabled: true }],
      environment: [null, Validators.required],
      version: [null, Validators.required],
      message: [null],
      update_status: [null, Validators.required],
      status: [null, Validators.required],
    });

    this.activatedRoute.queryParams.subscribe(params => {
      if (params['project']) {
        let field = this.findOptions.filters?.find(f => f.key == 'id_commit');
        if (field) {
          let option = field.options?.find(f => f.value == params['project']);
          field.value = option;
          utils.saveFindOptionsStorage(this.findOptions);
        }
      }
    });
  }

  revisions: TicketRevision[] = [];
  totalItems = 0;
  loading: boolean = false;
  currentTicketId: string | undefined;
  modalVisible: boolean = false;
  ticketVersionInfoVisible: boolean = false;
  formGroup!: FormGroup;
  updateLoading: boolean = false;
  hasProject: boolean = false;

  environmentOptions = [
    { value: 'production', label: 'Produção' },
    { value: 'homologation', label: 'Homologação' },
  ];

  updateStatusOptions = [
    { value: true, label: 'Sim' },
    { value: false, label: 'Não' },
  ];

  @ViewChild('table') table?: ResponsiveTableComponent;
  @ViewChild('filter') filter?: FilterWidgetComponent;
  @ViewChild('paginator') paginator!: PaginatorComponent;

  findOptions: FindOptions = {
    storageKey: 'update-assistant-find-options',
    q: '',
    fields: [
      {
        label: 'Chamado',
        columnWidth: 300,
      },
      {
        label: 'Cliente',
        columnWidth: 230,
      },
      {
        label: 'Sistema',
      },
      {
        label: 'Projeto',
      },
      {
        label: 'Versão',
      },
      {
        label: 'Status',
      },
      {
        label: 'Desenvolvedor',
      },
      {
        label: '',
        columnWidth: 35,
      },
    ],
    withs: [
      'ticket.system',
      'ticket.project',
      'ticket.ticketStatus',
      'ticket.customer.person',
      'ticket.developers',
    ],
    filters: [
      new FilterField({
        name: 'ticket_situation',
        key: 'tickets.situation',
        label: 'Situação chamado',
        value: 'A',
        visible: false,
      }),
      new FilterField({
        name: 'is_web',
        key: 'is_web',
        operator: FilterOperator.custom,
        label: 'Responsável',
        value: 1,
        visible: false,
      }),
      new FilterField({
        name: 'id_commit',
        label: 'Projeto',
        type: 'select',
        options: [
          { value: 'multipanel', label: 'Multipanel' },
          { value: 'redline', label: 'Redline' },
          { value: 'vendorflutter', label: 'Vendor' },
          { value: 'teste-pipeline', label: 'Teste-pipeline' },
        ],
        bindLabel: 'label',
        bindValue: 'value',
      }),
      new FilterField({
        name: 'version_eq',
        key: 'version',
        label: 'Versão',
      }),
      new FilterField({
        name: 'version_gte',
        key: 'version',
        label: 'Versão maior ou igual a',
        operator: FilterOperator.gte,
      }),
      new FilterField({
        name: 'version_lte',
        key: 'version',
        label: 'Versão menor ou igual a',
        operator: FilterOperator.lte,
      }),
      new FilterField({
        name: 'ticket_number_teorema',
        key: 'tickets.ticket_number_teorema',
        label: 'Chamado',
      }),
      new FilterField({
        name: 'status',
        label: 'Status',
        key: 'tickets.ticket_status_id',
        type: 'select',
        bindValue: 'id',
        bindLabel: 'description',
        modelName: 'ticket-statuses',
      }),
    ],
    reset: 1,
  };

  async getRevisions() {
    this.revisions = [];
    this.loading = true;
    this.hasProject = this.findOptions.filters?.find(f => f.key == 'id_commit')?.value != undefined;

    if (!this.hasProject) return;

    const response = await this.revisionService.findAll(this.findOptions).catch(() => {
      this.toastService.show({ color: 'error', message: 'Houve um erro ao carregar os dados' });
    });

    if (response?.data) {
      this.revisions = response.data;
    }

    if (response?.meta?.total != null && response?.meta?.total >= 0) {
      this.totalItems = response.meta.total;
    }

    this.loading = false;
  }

  async paginate(findOptions?: FindOptions) {
    if (findOptions) this.findOptions = { ...this.findOptions, ...findOptions };

    await this.getRevisions();
  }

  goToFirstPage() {
    if (this.table) this.table.checkedItems = [];

    this.paginate({ page: 1 });
    this.paginator.component.first = 0;
  }

  openTicketVersionModal() {
    this.formGroup.patchValue({
      project: this.project,
    });

    this.ticketVersionInfoVisible = false;
    setTimeout(() => {
      this.ticketVersionInfoVisible = true;
    }, 10);
  }

  openTicketModal(revision: any) {
    this.currentTicketId = revision.ticket?.id;
    this.modalVisible = true;
  }

  onModalVisibleChange(visible: boolean) {
    this.modalVisible = visible;
    if (!visible) {
      this.currentTicketId = undefined;
    }
  }

  onTicketUpdated(updatedTicket: TicketDetail) {
    this.revisions = this.revisions.map(revision => {
      if (revision.ticket?.id !== updatedTicket.id) return revision;
      return {
        ...revision,
        ticket: {
          ...revision.ticket,
          ticket_status: updatedTicket.status as any,
          summary: updatedTicket.summary ?? revision.ticket.summary,
        },
      };
    });
  }

  updateTicketsVersionInfo() {
    let formValue = this.formGroup.value;
    if (formValue.update_status == false && formValue.environment) {
      this.onEnvironmentChange(formValue.environment);
    }

    if (this.formGroup.invalid) {
      Object.keys(this.formGroup.controls).forEach(key => {
        this.formGroup.controls[key].markAsDirty();
      });

      this.toastService.show({
        color: 'error',
        message: 'Parece que há um ou mais campos inválidos no formulário',
      });
      return;
    }

    this.updateLoading = true;

    let ticket_ids: string[] = this.checkedTickets.map(ticket => ticket.id);

    let data: any = {
      ticket_ids,
      project: this.project,
      version: formValue.version,
      message: formValue.message,
      environment: formValue.environment,
      update_status: formValue.update_status,
      status: formValue.status,
      user_email: utils.getUser().email,
    };

    this.ticketService
      .updateTicketsVersionInfo(data)
      .then(res => {
        this.toastService.show({ color: 'success', message: 'Os chamados foram atualizados' });
      })
      .catch(() => {
        this.toastService.show({
          color: 'error',
          message: 'Houve um erro ao atualizar os chamados',
        });
      })
      .finally(() => {
        this.updateLoading = false;
        this.ticketVersionInfoVisible = false;
        this.formGroup.reset();
        this.getRevisions();
      });
  }

  onEnvironmentChange(env: string) {
    if (env == 'homologation') {
      this.formGroup.get('status')?.setValue({
        id: 'cd9267b5-1272-4ec5-a528-c8032cc7eb84',
        description: 'Testar',
      });
    }
    if (env == 'production') {
      this.formGroup.get('status')?.setValue({
        id: 'ab35c716-d03e-467a-8025-3c84f8a711a1',
        description: 'ATUALIZAÇÃO DE RELEASE',
      });
    }
  }

  get checkedTickets() {
    let tickets: Ticket[] = [];
    this.table?.checkedItems.forEach((revision: any) => {
      let index = tickets.findIndex(t => t.id == revision.ticket_id);
      if (index == -1) {
        tickets.push(revision.ticket);
      }
    });
    return tickets;
  }

  get project() {
    const id_commit = this.filter?.formGroup.get('id_commit')?.value;

    return id_commit?.value ?? null;
  }

  getStatusClass(status: string | undefined): PatronColors {
    if (!status) return 'secondary';
    return this.ticketService.getTicketStatusClass(status) as PatronColors;
  }

  isUpdateAssistantCanWrite() {
    return utils.checkAccess(['UpdateAssistant'], true, true).result;
  }
}
